import { useEffect, useState, type FormEvent } from "react";
import {
  Plus,
  ShieldCheck,
  UserRound,
  X,
  Pencil,
  Ban,
  Check,
} from "lucide-react";
import { api, json } from "./api";
import { Empty, Modal, Spinner, dateLabel } from "./components";
import type { User } from "./types";

type ManagedUser = User & {
  active: boolean;
  createdAt: string;
  updatedAt: string;
  projectCount: number;
};

export function AdminUsers({
  current,
  close,
}: {
  current: User;
  close: () => void;
}) {
  const [users, setUsers] = useState<ManagedUser[]>([]),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [editing, setEditing] = useState<ManagedUser | null | undefined>(),
    [busyId, setBusyId] = useState("");
  async function load() {
    setUsers(await api<ManagedUser[]>("/admin/users"));
  }
  useEffect(() => {
    let alive = true;
    api<ManagedUser[]>("/admin/users")
      .then((result) => {
        if (alive) setUsers(result);
      })
      .catch((e) => {
        if (alive) setError(e.message);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);
  async function toggle(user: ManagedUser) {
    setBusyId(user.id);
    setError("");
    try {
      await api(
        `/admin/users/${user.id}`,
        json("PATCH", {
          username: user.username,
          role: user.role,
          active: !user.active,
          password: "",
        }),
      );
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId("");
    }
  }
  return (
    <div className="admin-overlay">
      <header>
        <div>
          <span className="eyebrow">АДМИНИСТРИРОВАНИЕ</span>
          <h1>Пользователи</h1>
        </div>
        <div className="history-actions">
          <button className="primary" onClick={() => setEditing(null)}>
            <Plus size={16} />
            Добавить пользователя
          </button>
          <button className="secondary" onClick={close}>
            <X size={16} />
            Закрыть
          </button>
        </div>
      </header>
      <main>
        <p className="muted admin-intro">
          У каждого пользователя своё личное пространство проектов. Блокировка
          завершает его сеансы и сохраняет все данные.
        </p>
        {error && (
          <div className="error-message" role="alert">
            {error}
          </div>
        )}
        {loading ? (
          <div className="loading">
            <Spinner />
          </div>
        ) : users.length ? (
          <div className="user-list">
            {users.map((user) => (
              <article
                className={`user-row ${user.active ? "" : "inactive"}`}
                key={user.id}
              >
                <span className="user-avatar">
                  <UserRound size={19} />
                </span>
                <div className="user-copy">
                  <div>
                    <strong>{user.username}</strong>
                    {user.id === current.id && (
                      <span className="self-label">Это вы</span>
                    )}
                  </div>
                  <span>
                    {user.role === "admin" ? "Администратор" : "Пользователь"} ·
                    проектов {user.projectCount} · с {dateLabel(user.createdAt)}
                  </span>
                </div>
                <span
                  className={`account-state ${user.active ? "active" : "blocked"}`}
                >
                  {user.active ? "Активен" : "Заблокирован"}
                </span>
                <button className="secondary" onClick={() => setEditing(user)}>
                  <Pencil size={14} />
                  Изменить
                </button>
                <button
                  className="text-button"
                  disabled={user.id === current.id || busyId === user.id}
                  onClick={() => void toggle(user)}
                >
                  {busyId === user.id ? (
                    <Spinner />
                  ) : user.active ? (
                    <Ban size={14} />
                  ) : (
                    <Check size={14} />
                  )}{" "}
                  {user.active ? "Заблокировать" : "Разблокировать"}
                </button>
              </article>
            ))}
          </div>
        ) : (
          <Empty title="Пользователей пока нет" />
        )}
      </main>
      {editing !== undefined && (
        <UserForm
          initial={editing || undefined}
          current={current}
          close={() => setEditing(undefined)}
          saved={async (ownPasswordChanged) => {
            setEditing(undefined);
            if (ownPasswordChanged) location.reload();
            else await load();
          }}
        />
      )}
    </div>
  );
}

function UserForm({
  initial,
  current,
  close,
  saved,
}: {
  initial?: ManagedUser;
  current: User;
  close: () => void;
  saved: (ownPasswordChanged: boolean) => Promise<void>;
}) {
  const [username, setUsername] = useState(initial?.username || ""),
    [role, setRole] = useState<User["role"]>(initial?.role || "user"),
    [password, setPassword] = useState(""),
    [repeat, setRepeat] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (password !== repeat) {
      setError("Пароли не совпадают.");
      return;
    }
    if (!initial && !password) {
      setError("Задай временный пароль.");
      return;
    }
    setBusy(true);
    try {
      if (initial)
        await api(
          `/admin/users/${initial.id}`,
          json("PATCH", { username, role, active: initial.active, password }),
        );
      else
        await api("/admin/users", json("POST", { username, role, password }));
      await saved(!!initial && initial.id === current.id && !!password);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title={initial ? "Изменить пользователя" : "Новый пользователь"}
      close={close}
    >
      <form onSubmit={submit}>
        <label>
          Логин
          <input
            autoFocus
            required
            minLength={3}
            maxLength={64}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Например, level_designer"
          />
        </label>
        <label>
          Роль
          <select
            value={role}
            disabled={initial?.id === current.id}
            onChange={(e) => setRole(e.target.value as User["role"])}
          >
            <option value="user">Пользователь</option>
            <option value="admin">Администратор</option>
          </select>
        </label>
        <label>
          {initial ? "Новый пароль" : "Временный пароль"}
          <input
            type="password"
            autoComplete="new-password"
            required={!initial}
            minLength={password ? 12 : undefined}
            maxLength={128}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={
              initial
                ? "Оставь пустым, чтобы не менять"
                : "Не менее 12 символов"
            }
          />
        </label>
        <label>
          Повтори пароль
          <input
            type="password"
            autoComplete="new-password"
            required={!!password}
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
          />
        </label>
        {initial?.id === current.id && password && (
          <div className="notice">
            После смены своего пароля нужно будет войти заново.
          </div>
        )}
        {error && (
          <div className="error-message" role="alert">
            {error}
          </div>
        )}
        <div className="modal-actions">
          <button
            type="button"
            className="secondary"
            disabled={busy}
            onClick={close}
          >
            Отмена
          </button>
          <button className="primary" disabled={busy || !username.trim()}>
            {busy ? <Spinner /> : <ShieldCheck size={15} />}Сохранить
          </button>
        </div>
      </form>
    </Modal>
  );
}
